import './App.css';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import HomePage from './pages/HomePage';
import MobileCallBar from './components/site/MobileCallBar';

// Route-level code splitting: only the Home route ships in the initial bundle.
// Every other page loads on demand, cutting unused JavaScript on first paint.
const ServiceAreasIndex = lazy(() => import('./pages/ServiceAreasIndex'));
const SuburbPage = lazy(() => import('./pages/SuburbPage'));
const CommercialPage = lazy(() => import('./pages/CommercialPage'));
const CommercialRegionPage = lazy(() => import('./pages/CommercialRegionPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const LegacyRedirect = lazy(() => import('./pages/LegacyRedirect'));

// Customer portal (hidden feature — intentionally NOT linked anywhere in the
// public site nav/footer. Reachable only by direct URL until launch).
const PortalLogin = lazy(() => import('./pages/portal/PortalLogin'));
const PortalSignup = lazy(() => import('./pages/portal/PortalSignup'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalForgot = lazy(() => import('./pages/portal/PortalForgot'));
const PortalReset = lazy(() => import('./pages/portal/PortalReset'));
const PortalAdminLogin = lazy(() => import('./pages/portal/PortalAdminLogin'));
const PortalAdminView = lazy(() => import('./pages/portal/PortalAdminView'));

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/commercial" element={<CommercialPage />} />
          <Route path="/commercial/:region" element={<CommercialRegionPage />} />
          <Route path="/service-areas" element={<ServiceAreasIndex />} />
          <Route path="/service-areas/:slug" element={<SuburbPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Hidden customer portal — not linked from the public site yet. */}
          <Route path="/portal" element={<PortalLogin />} />
          <Route path="/portal/signup" element={<PortalSignup />} />
          <Route path="/portal/dashboard" element={<PortalDashboard />} />
          <Route path="/portal/forgot" element={<PortalForgot />} />
          <Route path="/portal/reset" element={<PortalReset />} />
          <Route path="/portal/admin" element={<PortalAdminLogin />} />
          <Route path="/portal/admin/view" element={<PortalAdminView />} />

          {/* Redirects for common legacy / prior-site paths so Google's 39 404s
              become permanent redirects that pass link equity to the right pages. */}
          <Route path="/contact" element={<Navigate to="/#contact" replace />} />
          <Route path="/contact-us" element={<Navigate to="/#contact" replace />} />
          <Route path="/quote" element={<Navigate to="/#contact" replace />} />
          <Route path="/get-a-quote" element={<Navigate to="/#contact" replace />} />
          <Route path="/free-quote" element={<Navigate to="/#contact" replace />} />
          <Route path="/book" element={<Navigate to="/#contact" replace />} />
          <Route path="/booking" element={<Navigate to="/#contact" replace />} />

          <Route path="/about" element={<Navigate to="/#about" replace />} />
          <Route path="/about-us" element={<Navigate to="/#about" replace />} />
          <Route path="/our-story" element={<Navigate to="/#about" replace />} />

          <Route path="/services" element={<Navigate to="/#services" replace />} />
          <Route path="/services/*" element={<Navigate to="/#services" replace />} />
          <Route path="/residential" element={<Navigate to="/#services" replace />} />
          <Route path="/residential-services" element={<Navigate to="/#services" replace />} />
          <Route path="/lawn-mowing" element={<Navigate to="/#services" replace />} />
          <Route path="/gardening" element={<Navigate to="/#services" replace />} />
          <Route path="/garden-maintenance" element={<Navigate to="/#services" replace />} />
          <Route path="/weeding" element={<Navigate to="/#services" replace />} />
          <Route path="/weed-control" element={<Navigate to="/#services" replace />} />
          <Route path="/lawn-care" element={<Navigate to="/#services" replace />} />
          <Route path="/hedging-tree-trimming" element={<Navigate to="/#services" replace />} />
          <Route path="/tree-trimming" element={<Navigate to="/#services" replace />} />
          <Route path="/hedging" element={<Navigate to="/#services" replace />} />

          {/* Legacy GoHighLevel / funnel paths — old marketing pages that
              Google still remembers from the previous vendor. */}
          <Route path="/thank-you" element={<Navigate to="/" replace />} />
          <Route path="/thankyou" element={<Navigate to="/" replace />} />
          <Route path="/thanks" element={<Navigate to="/" replace />} />
          <Route path="/success" element={<Navigate to="/" replace />} />
          <Route path="/opt-in" element={<Navigate to="/#contact" replace />} />
          <Route path="/optin" element={<Navigate to="/#contact" replace />} />
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/lp" element={<Navigate to="/" replace />} />
          <Route path="/funnel" element={<Navigate to="/" replace />} />
          <Route path="/promo" element={<Navigate to="/#contact" replace />} />
          <Route path="/discount" element={<Navigate to="/#contact" replace />} />
          <Route path="/get-your-discount" element={<Navigate to="/#contact" replace />} />
          <Route path="/new-customer" element={<Navigate to="/#contact" replace />} />
          <Route path="/booking-calendar" element={<Navigate to="/#contact" replace />} />
          <Route path="/schedule" element={<Navigate to="/#contact" replace />} />
          <Route path="/calendar" element={<Navigate to="/#contact" replace />} />
          <Route path="/enquiry" element={<Navigate to="/#contact" replace />} />
          <Route path="/enquire" element={<Navigate to="/#contact" replace />} />
          <Route path="/marketing-form" element={<Navigate to="/#contact" replace />} />

          <Route path="/gallery" element={<Navigate to="/#gallery" replace />} />
          <Route path="/projects" element={<Navigate to="/commercial" replace />} />
          <Route path="/portfolio" element={<Navigate to="/commercial" replace />} />

          <Route path="/commercial-services" element={<Navigate to="/commercial" replace />} />
          <Route path="/business" element={<Navigate to="/commercial" replace />} />

          <Route path="/reviews" element={<Navigate to="/#testimonials" replace />} />
          <Route path="/testimonials" element={<Navigate to="/#testimonials" replace />} />

          <Route path="/areas" element={<Navigate to="/service-areas" replace />} />
          <Route path="/locations" element={<Navigate to="/service-areas" replace />} />
          <Route path="/suburbs" element={<Navigate to="/service-areas" replace />} />

          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/index" element={<Navigate to="/" replace />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="/index.php" element={<Navigate to="/" replace />} />

          {/* Catch-all: matches legacy vendor URL shapes and redirects; otherwise renders NotFoundPage. */}
          <Route path="*" element={<LegacyRedirect />} />
        </Routes>
        </Suspense>
        <MobileCallBar />
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
